// McpApprovals — 受治理 MCP 工具呼叫的「人工核准收件匣」。
//
// 列出本客戶單位待核准的高風險 MCP 工具呼叫；每列可「核准 / 拒絕」。
// 核准 = 以「原提出者」的身分執行該高風險動作；雙人覆核：不能核准自己
// 提出的呼叫（後端確定性重新比對，自核准回 403）。
//
// 鏡像對象：apps/frontend/src/dashboard/pages/Audit.tsx（PageHeader + 重新
// 整理 + loading/error/empty/ready 四態）與 SwapRequests.tsx（每列審核動作）。
// 後端：apps/runtime-api/src/index.ts /v1/runtime/mcp-tool-call-approvals。
// Spec：docs/specs/nexus-mcp-approval-audit-v0.md。
//
// 顯示永遠是 redacted 後的摘要：工具名 + 提出者 + 參數鍵名/數量
// （NEVER 參數值）+ 時間。原始參數 / 密鑰 / correlation_token 不會到前端。

import React, { useCallback, useEffect, useState } from "react";
import {
  RefreshCw,
  ServerCrash,
  ShieldQuestion,
  ThumbsDown,
  ThumbsUp,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import Skeleton from "../components/Skeleton.js";
import {
  listMcpApprovals,
  decideMcpApproval,
  friendlyMcpApprovalError,
  type McpApprovalRow,
  type McpVerdict,
} from "../lib/mcpApprovalsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";
import { formatTaipeiDateTime } from "../../lib/time.js";

type ListState =
  | { status: "loading" }
  | { status: "ready"; items: McpApprovalRow[] }
  | { status: "error"; message: string };

/** Per-decision outcome banner state (e.g. approved-but-execute-failed). */
type Outcome = {
  kind: "success" | "warning" | "error";
  text: string;
};

/** Plain-language relative hint for a deadline: "X 分鐘後逾時" / 已逾時. */
function deadlineHint(deadline: string | null): { text: string; overdue: boolean } | null {
  if (!deadline) return null;
  const t = Date.parse(deadline);
  if (Number.isNaN(t)) return null;
  const diffSec = Math.floor((t - Date.now()) / 1000);
  if (diffSec <= 0) return { text: "已逾時（背景作業會自動關閉）", overdue: true };
  if (diffSec < 60) return { text: `約 ${diffSec} 秒後逾時`, overdue: false };
  if (diffSec < 3600) return { text: `約 ${Math.floor(diffSec / 60)} 分鐘後逾時`, overdue: false };
  if (diffSec < 86400) return { text: `約 ${Math.floor(diffSec / 3600)} 小時後逾時`, overdue: false };
  return { text: `約 ${Math.floor(diffSec / 86400)} 天後逾時`, overdue: false };
}

export default function McpApprovals(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const { confirm } = useConfirmDialog();

  const [state, setState] = useState<ListState>({ status: "loading" });
  const [busyId, setBusyId] = useState<string | null>(null);
  // Per-row outcome banners keyed by approval id (cleared on refresh / re-decide).
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listMcpApprovals({ tenantId: tenantParam, limit: 100 });
      setState({ status: "ready", items: r.approvals });
    } catch (err) {
      setState({ status: "error", message: friendlyMcpApprovalError(err) });
    }
  }, [tenantParam]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(
    async (row: McpApprovalRow, verdict: McpVerdict) => {
      // High-risk gate: approving runs the tool as the original requester.
      const ok = await confirm(
        verdict === "approved"
          ? `確定要核准「${row.tool_name}」嗎？\n\n核准後系統會以原提出者（${row.actor_user_id}・${row.actor_role}）的身分執行這個高風險動作。此決議會原樣記錄到操作紀錄。`
          : `確定要拒絕「${row.tool_name}」嗎？\n\n拒絕後此呼叫不會被執行；此決議會原樣記錄到操作紀錄。`,
        {
          title: verdict === "approved" ? "核准高風險 MCP 工具呼叫" : "拒絕 MCP 工具呼叫",
          okLabel: verdict === "approved" ? "核准並執行" : "拒絕",
          cancelLabel: "取消",
          destructive: verdict === "approved",
        },
      );
      if (!ok) return;

      setBusyId(row.id);
      setOutcomes((m) => {
        const { [row.id]: _drop, ...rest } = m;
        void _drop;
        return rest;
      });
      try {
        const res = await decideMcpApproval(row.id, verdict, tenantParam);
        if (res.status === "rejected") {
          setOutcomes((m) => ({ ...m, [row.id]: { kind: "success", text: "已拒絕。此呼叫不會被執行。" } }));
        } else if (res.executed) {
          setOutcomes((m) => ({ ...m, [row.id]: { kind: "success", text: "已核准並成功執行。" } }));
        } else {
          // Approved but the actor-replay execution did NOT run (fail-closed,
          // surfaced — never a fabricated run). Show the reason verbatim.
          setOutcomes((m) => ({
            ...m,
            [row.id]: {
              kind: "warning",
              text: `已核准，但執行未完成${res.reason ? `（原因：${res.reason}）` : ""}。核准已生效並記錄；執行會由系統重試 / 需人工排查。`,
            },
          }));
        }
        // Refresh the queue — the decided row leaves the pending list.
        await load();
      } catch (err) {
        setOutcomes((m) => ({ ...m, [row.id]: { kind: "error", text: friendlyMcpApprovalError(err) } }));
      } finally {
        setBusyId(null);
      }
    },
    [confirm, tenantParam, load],
  );

  return (
    <>
      <PageHeader
        title="MCP 工具核准"
        description={
          session.super_admin && !tenantParam
            ? "您是平台管理員。請先選定一個客戶單位（進入該單位身分）才能看到其待核准的 MCP 工具呼叫。"
            : "外部 AI 透過 MCP 提出的高風險工具呼叫會被攔下，等候人工核准。核准等於以原提出者身分執行該動作；為了雙人覆核，您不能核准自己提出的呼叫。"
        }
        actions={
          <button
            className="btn-secondary text-sm"
            onClick={() => void load()}
            disabled={state.status === "loading"}
            data-tour-id="mcp-approvals.refresh"
          >
            <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />

      {state.status === "loading" && (
        <div className="card p-5 space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      )}

      {state.status === "error" && (
        <EmptyState
          icon={ServerCrash}
          title="核准清單載入失敗"
          description={state.message}
          action={
            <button className="btn-primary text-sm" onClick={() => void load()}>
              再試一次
            </button>
          }
        />
      )}

      {state.status === "ready" && state.items.length === 0 && (
        <EmptyState
          icon={ShieldQuestion}
          title="目前沒有待核准的 MCP 工具呼叫"
          description="當外部 AI 提出需人工核准的高風險工具呼叫時，會即時出現在這裡。"
        />
      )}

      {state.status === "ready" && state.items.length > 0 && (
        <div className="card divide-y divide-border" data-tour-id="mcp-approvals.list">
          {state.items.map((row) => (
            <ApprovalRow
              key={row.id}
              row={row}
              busy={busyId === row.id}
              anyBusy={busyId !== null}
              outcome={outcomes[row.id] ?? null}
              isOwn={!!session.user_id && row.actor_user_id === session.user_id}
              onDecide={(v) => void decide(row, v)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function ApprovalRow({
  row,
  busy,
  anyBusy,
  outcome,
  isOwn,
  onDecide,
}: {
  row: McpApprovalRow;
  busy: boolean;
  anyBusy: boolean;
  outcome: Outcome | null;
  isOwn: boolean;
  onDecide: (verdict: McpVerdict) => void;
}): React.ReactElement {
  const dl = deadlineHint(row.deadline);
  const OutcomeIcon = outcome
    ? outcome.kind === "success"
      ? CheckCircle2
      : outcome.kind === "warning"
        ? AlertTriangle
        : XCircle
    : null;
  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="badge-warning text-xs">待核准</span>
            <span className="font-mono text-sm font-medium text-text-primary truncate">{row.tool_name}</span>
          </div>

          <div className="mt-1.5 text-xs text-text-secondary">
            提出者：
            <span className="font-medium text-text-primary">{row.actor_user_id}</span>
            <span className="ml-1 text-text-muted">（{row.actor_role}）</span>
          </div>

          <div className="mt-1.5 text-xs text-text-secondary">
            參數：共 {row.arg_count} 個
            {row.arg_keys.length > 0 && (
              <span className="ml-1.5 inline-flex flex-wrap gap-1 align-middle">
                {row.arg_keys.map((k) => (
                  <span key={k} className="rounded bg-bg-raised border border-border px-1.5 py-0.5 font-mono text-2xs text-text-muted">
                    {k}
                  </span>
                ))}
              </span>
            )}
            <span className="ml-1 text-text-muted">（僅顯示參數名稱，不顯示內容）</span>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-text-muted">
            <span className="font-mono">提出於 {formatTaipeiDateTime(row.created_at)}</span>
            {dl && (
              <span className={`inline-flex items-center gap-1 ${dl.overdue ? "text-danger" : ""}`}>
                <Clock className="h-3 w-3" /> {dl.text}
              </span>
            )}
          </div>

          {isOwn && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded border border-warning/40 bg-warning/10 px-2 py-1 text-2xs text-warning">
              <AlertTriangle className="h-3 w-3" /> 這是您自己提出的呼叫，依雙人覆核規則無法由您核准（需另一位有權限者）。
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-row items-center gap-2">
          <button
            onClick={() => onDecide("rejected")}
            className="btn-danger text-sm"
            disabled={busy || anyBusy}
          >
            <ThumbsDown className="h-4 w-4" /> 拒絕
          </button>
          <button
            onClick={() => onDecide("approved")}
            className="btn-primary text-sm"
            disabled={busy || anyBusy || isOwn}
            title={isOwn ? "不能核准自己提出的呼叫（雙人覆核）" : undefined}
          >
            <ThumbsUp className="h-4 w-4" /> 核准
          </button>
        </div>
      </div>

      {outcome && OutcomeIcon && (
        <div
          className={`mt-2 flex items-start gap-2 rounded border p-2 text-xs ${
            outcome.kind === "success"
              ? "border-success/40 bg-success/10 text-success"
              : outcome.kind === "warning"
                ? "border-warning/40 bg-warning/10 text-warning"
                : "border-danger/40 bg-danger/10 text-danger"
          }`}
        >
          <OutcomeIcon className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{outcome.text}</span>
        </div>
      )}
    </div>
  );
}
